import type { AwsCredentials, AuthProvider } from './types';

const REFRESH_BUFFER_MS = 5 * 60 * 1000;

let cache: AwsCredentials | null = null;
let inflight: Promise<AwsCredentials> | null = null;

// AuthProvider responses commonly travel through `JSON.parse(await response.text())`,
// which leaves `expiration` as an ISO string even though the type says Date. Coerce
// defensively so `.getTime()` never throws on a real consumer path.
const toDate = (v: unknown): Date => {
  if (v instanceof Date && !isNaN(v.getTime())) return v;
  if (typeof v === 'string' || typeof v === 'number') {
    const d = new Date(v);
    if (!isNaN(d.getTime())) return d;
  }
  throw new Error(`[ecs-telemetry] invalid expiration value from authProvider: ${String(v)}`);
};

const normalize = (creds: AwsCredentials): AwsCredentials => ({
  ...creds,
  expiration: toDate(creds.expiration),
});

export const getCredentials = async (provider: AuthProvider): Promise<AwsCredentials> => {
  if (cache && cache.expiration.getTime() - Date.now() > REFRESH_BUFFER_MS) {
    return cache;
  }
  if (inflight) return inflight;
  inflight = provider()
    .then((creds) => {
      const normalized = normalize(creds);
      cache = normalized;
      inflight = null;
      return normalized;
    })
    .catch((err) => {
      inflight = null;
      throw err;
    });
  return inflight;
};

export const _resetForTests = (): void => {
  cache = null;
  inflight = null;
};
